import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { categoryApi, Category } from '../api/category.api';

interface CategoryContextType {
  categories: Category[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

const CategoryContext = createContext<CategoryContextType | undefined>(undefined);

interface CategoryProviderProps {
  children: ReactNode;
}

export const CategoryProvider = ({ children }: CategoryProviderProps) => {
  const { data, isLoading, isError, error } = useQuery<Category[], Error>({
    queryKey: ['categories'],
    queryFn: async () => {
      const fetchedCategories = await categoryApi.getCategories();
      return [...fetchedCategories];
    },
    staleTime: Infinity, // 카테고리 데이터는 거의 변하지 않으므로 staleTime을 무한대로 설정
    gcTime: Infinity, // 캐시 시간도 무한대로 설정
  });

  const value = useMemo(
    () => ({
      categories: data ?? [],
      isLoading,
      isError,
      error,
    }),
    [data, isLoading, isError, error],
  );

  return <CategoryContext.Provider value={value}>{children}</CategoryContext.Provider>;
};

export const useCategories = () => {
  const context = useContext(CategoryContext);
  if (context === undefined) {
    throw new Error('useCategories must be used within a CategoryProvider');
  }
  return context;
};
